"use client";

import { useEffect, useRef, useState } from "react";

const outputStatusStyles = {
  executed: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  tracking: "bg-blue/5 text-blue ring-blue/15",
  review: "bg-amber-50 text-amber-700 ring-amber-200",
} as const;

const previewStatusStyles = {
  executed: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  tracking: "bg-blue/5 text-blue ring-blue/15",
  review: "bg-amber-50 text-amber-700 ring-amber-200",
} as const;

const outputItems = [
  {
    label: "Market",
    title: "Competitor watch",
    body: "What incumbents are now emphasizing and what that means for your angle.",
    status: "review",
    statusLabel: "Needs approval",
    previewEyebrow: "Ready for approval",
    previewTitle: "Shift the message toward post-sale ownership risk.",
    previewBody:
      "Lucien found competitors moving from generic automation claims toward handoff-risk language. Approve this and Lucien updates the page angle, outreach premise, and watchlist.",
    previewSteps: [
      "Refresh the competitor watchlist around handoff-risk claims",
      "Rewrite the angle around post-sale ownership risk",
      "Track buyer replies against the old and new positioning",
    ],
    primaryAction: "Approve",
    secondaryAction: "Review",
  },
  {
    label: "Segment",
    title: "Buyer targets",
    body: "The people or segments Lucien ranked first, with the signal behind the choice.",
    status: "executed",
    statusLabel: "Executed",
    previewEyebrow: "Executed",
    previewTitle: "Built the first buyer list from repeated pain.",
    previewBody:
      "Lucien ranked ops leaders at 50-200 person SaaS companies highest because they repeated the handoff pain, had budget ownership, and were reachable this week.",
    previewSteps: [
      "Scored three buyer segments by urgency and reachability",
      "Found 42 matching leads with email or LinkedIn paths",
      "Moved 12 strongest matches into the outreach test",
    ],
    primaryAction: "View list",
    secondaryAction: "See reasoning",
  },
  {
    label: "Claims",
    title: "Claim audit",
    body: "The page claims Lucien can prove, weaken, or rewrite from replies, calls, and competitors.",
    status: "executed",
    statusLabel: "Executed",
    previewEyebrow: "Executed",
    previewTitle: "Audited the claims buyers can actually believe.",
    previewBody:
      "Lucien separated claims backed by buyer language from claims that only sounded plausible internally, then marked the weak claims for rewrite or proof.",
    previewSteps: [
      "Mapped each page claim to replies, call notes, and usage signal",
      "Flagged unsupported automation claims as trust risks",
      "Kept the claims buyers repeated in their own words",
    ],
    primaryAction: "View audit",
    secondaryAction: "See signal",
  },
  {
    label: "Action",
    title: "Executable next move",
    body: "One focused rewrite, follow-up, interview, or test ready for approval and execution.",
    status: "review",
    statusLabel: "Needs review",
    previewEyebrow: "Queued for review",
    previewTitle: "Run a budget-risk test before the next build cycle.",
    previewBody:
      "Lucien prepared a narrow test to learn whether handoff pain is urgent enough to create budget, not just curiosity. Approval keeps the founder in control before anything buyer-facing runs.",
    previewSteps: [
      "Ask 12 matched leads how handoff failures are paid for today",
      "Send one follow-up that tests budget, owner, and timing",
      "Use replies to decide whether to rewrite, narrow, or stop",
    ],
    primaryAction: "Approve",
    secondaryAction: "Review",
  },
  {
    label: "Copy",
    title: "Positioning rewrite",
    body: "Hero, subhead, offer, and CTA grounded in buyer language.",
    status: "executed",
    statusLabel: "Executed",
    previewEyebrow: "Executed",
    previewTitle: "Rewrote the page around the painful moment.",
    previewBody:
      "Lucien replaced broad workflow language with the buyer's actual trigger: the ownership mess that appears after onboarding and keeps ops teams exposed.",
    previewSteps: [
      "Changed the hero from feature language to buyer pain",
      "Reframed the offer around ownership risk after implementation",
      "Updated the CTA to route visitors into the right test",
    ],
    primaryAction: "View rewrite",
    secondaryAction: "Compare copy",
  },
  {
    label: "Outbound",
    title: "Outreach sequence",
    body: "Short messages Lucien can send after approval to test pain, budget, and urgency.",
    status: "tracking",
    statusLabel: "Tracking results",
    previewEyebrow: "Tracking results",
    previewTitle: "Sent the handoff-risk sequence and is reading replies.",
    previewBody:
      "Lucien is tracking whether buyers respond with budget language, process ownership, or polite curiosity so the next move is based on signal, not hope.",
    previewSteps: [
      "Sent 12 approved founder-to-founder messages",
      "Tagged replies by urgency, budget, owner, and objection",
      "Preparing the next follow-up from the strongest replies",
    ],
    primaryAction: "View replies",
    secondaryAction: "Tune test",
  },
] as const;

type OutputItem = (typeof outputItems)[number];
type PreviewAction = "primary" | "secondary";

function getPreviewNotice(item: OutputItem, action: PreviewAction) {
  if (action === "secondary") {
    return "Review mode: Lucien would show the signal, assumptions, and limits behind this move.";
  }

  if (item.primaryAction === "Approve") {
    return "Approved in this preview: Lucien would execute this scoped move and track the result.";
  }

  return "Opened in this preview: Lucien would show the prepared artifact and the signal behind it.";
}

function PreviewActions({
  item,
  onAction,
}: Readonly<{ item: OutputItem; onAction: (action: PreviewAction) => void }>) {
  const isReview = item.status === "review";

  return (
    <div className="mt-5 flex flex-wrap gap-2">
      <button
        type="button"
        onClick={() => onAction("secondary")}
        className={`inline-flex h-9 items-center justify-center rounded-full px-4 text-xs font-semibold ${
          isReview
            ? "border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
            : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
        }`}
      >
        {item.secondaryAction}
      </button>
      <button
        type="button"
        onClick={() => onAction("primary")}
        className={`inline-flex h-9 items-center justify-center rounded-full px-4 text-xs font-semibold shadow-sm hover:brightness-95 ${
          item.primaryAction === "Approve"
            ? "bg-emerald-600 text-white shadow-emerald-950/10"
            : "bg-blue text-white"
        }`}
      >
        {item.primaryAction}
      </button>
    </div>
  );
}

export function OutputQueue() {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [previewOffset, setPreviewOffset] = useState(0);
  const [previewAction, setPreviewAction] = useState<PreviewAction | null>(null);
  const asideRef = useRef<HTMLElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedItem = outputItems[selectedIndex];

  useEffect(() => {
    let frame = 0;

    const updatePreviewOffset = () => {
      window.cancelAnimationFrame(frame);

      frame = window.requestAnimationFrame(() => {
        const row = rowRefs.current[selectedIndex];
        const aside = asideRef.current;
        const preview = previewRef.current;

        if (!row || !aside || !preview || window.innerWidth < 1024) {
          setPreviewOffset(0);
          return;
        }

        const rowRect = row.getBoundingClientRect();
        const asideRect = aside.getBoundingClientRect();
        const previewRect = preview.getBoundingClientRect();
        const asideStyles = window.getComputedStyle(aside);
        const asidePaddingTop = Number.parseFloat(asideStyles.paddingTop) || 0;
        const asidePaddingBottom = Number.parseFloat(asideStyles.paddingBottom) || 0;
        const previewHeight = previewRect.height;
        const targetPreviewTop = rowRect.top + rowRect.height / 2 - previewHeight / 2;
        const previewBaseTop = asideRect.top + asidePaddingTop;
        const maxOffset = Math.max(
          0,
          asideRect.height - asidePaddingTop - asidePaddingBottom - previewHeight
        );
        const minViewportTop = 108;
        const maxViewportBottom = window.innerHeight - 28;
        const minVisibleOffset = Math.max(0, minViewportTop - previewBaseTop);
        const maxVisibleOffset = Math.max(
          minVisibleOffset,
          maxViewportBottom - previewHeight - previewBaseTop
        );
        const previewOffsetLimit = Math.max(
          minVisibleOffset,
          Math.min(maxOffset, maxVisibleOffset)
        );
        const nextPreviewOffset = Math.min(
          Math.max(targetPreviewTop - previewBaseTop, minVisibleOffset),
          previewOffsetLimit
        );

        setPreviewOffset(nextPreviewOffset);
      });
    };

    updatePreviewOffset();

    const resizeObserver = new ResizeObserver(updatePreviewOffset);
    if (gridRef.current) resizeObserver.observe(gridRef.current);
    if (asideRef.current) resizeObserver.observe(asideRef.current);
    if (previewRef.current) resizeObserver.observe(previewRef.current);
    rowRefs.current.forEach(row => {
      if (row) resizeObserver.observe(row);
    });

    window.addEventListener("resize", updatePreviewOffset);
    window.addEventListener("scroll", updatePreviewOffset, { passive: true });

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      window.removeEventListener("resize", updatePreviewOffset);
      window.removeEventListener("scroll", updatePreviewOffset);
    };
  }, [selectedIndex]);

  return (
    <div className="mt-10 overflow-hidden rounded-[2rem] border border-blue/10 bg-white/90 shadow-[0_26px_80px_rgba(15,23,42,0.12)] backdrop-blur-sm">
      <div className="relative overflow-hidden border-b border-slate-200/80 px-4 py-4 sm:px-5">
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(circle_at_18%_0%,rgba(36,92,255,0.18),transparent_34%),linear-gradient(90deg,rgba(0,71,249,0.045)_1px,transparent_1px),linear-gradient(rgba(0,71,249,0.045)_1px,transparent_1px)] bg-[length:auto,44px_44px,44px_44px]"
        />
        <div className="relative flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-1 inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-blue text-xs font-bold text-white shadow-sm ring-4 ring-blue/10"
            >
              6
            </span>
            <div>
              <h3 className="text-base font-semibold tracking-tight text-slate-950">
                Action queue
              </h3>
              <p className="mt-0.5 max-w-2xl text-sm leading-5 text-slate-600">
                Buyer signal turned into work Lucien can view, run, track, or revise.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
              3 executed
            </span>
            <span className="inline-flex items-center rounded-full bg-blue/5 px-3 py-1.5 text-xs font-semibold text-blue ring-1 ring-blue/15">
              1 tracking
            </span>
            <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
              2 awaiting review
            </span>
          </div>
        </div>
      </div>

      <div ref={gridRef} className="relative grid lg:grid-cols-[1.08fr_0.92fr]">
        <div className="divide-y divide-slate-200/80">
          {outputItems.map((item, index) => {
            const isSelected = index === selectedIndex;

            return (
              <button
                key={item.title}
                ref={node => {
                  rowRefs.current[index] = node;
                }}
                type="button"
                aria-pressed={isSelected}
                onClick={() => {
                  setSelectedIndex(index);
                  setPreviewAction(null);
                }}
                className={`relative grid w-full gap-5 px-5 py-4 text-left transition-colors duration-200 hover:bg-blue/[0.035] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue/20 sm:grid-cols-[4.25rem_minmax(0,1fr)_3.75rem] sm:items-start sm:px-6 ${
                  isSelected ? "bg-blue/[0.055] shadow-[inset_4px_0_0_rgba(0,71,249,0.62)]" : ""
                }`}
              >
                {isSelected ? (
                  <span
                    aria-hidden="true"
                    className="absolute right-0 top-1/2 hidden h-14 w-1.5 -translate-y-1/2 rounded-l-full bg-blue/70 lg:block"
                  />
                ) : null}
                <div className="flex w-[4.25rem] shrink-0 items-center gap-3 sm:block">
                  <span
                    className={`inline-flex size-8 items-center justify-center rounded-full text-xs font-bold ring-1 ${
                      isSelected
                        ? "bg-blue text-white ring-blue"
                        : "bg-blue/10 text-blue ring-blue/15"
                    }`}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 sm:mt-3 sm:block">
                    {item.label}
                  </span>
                </div>
                <div className="min-w-0">
                  <div className="grid gap-2 sm:grid-cols-[minmax(0,10.5rem)_auto] sm:items-center">
                    <h3 className="text-base font-semibold tracking-tight text-slate-950">
                      {item.title}
                    </h3>
                    <span
                      className={`inline-flex w-fit items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${outputStatusStyles[item.status]}`}
                    >
                      {item.statusLabel}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm leading-6 text-slate-600">{item.body}</p>
                </div>
                <span
                  className={`inline-flex w-fit items-center rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${
                    isSelected
                      ? "border-blue bg-blue text-white"
                      : "border-blue/15 bg-blue/5 text-blue"
                  }`}
                >
                  View
                </span>
              </button>
            );
          })}
        </div>

        <aside
          ref={asideRef}
          className="border-t border-slate-200/80 bg-[linear-gradient(180deg,rgba(0,71,249,0.055),rgba(0,71,249,0.025))] p-5 sm:p-6 lg:border-l lg:border-t-0 lg:p-8"
        >
          <div
            ref={previewRef}
            className="relative z-20 rounded-3xl border border-slate-200 bg-white p-5 text-slate-950 shadow-[0_18px_50px_rgba(15,23,42,0.08)] transition-transform duration-300 ease-out"
            style={{ transform: `translateY(${previewOffset}px)` }}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue">
                {selectedItem.label}
              </p>
              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${previewStatusStyles[selectedItem.status]}`}
              >
                {selectedItem.statusLabel}
              </span>
            </div>
            <h3 className="mt-4 text-xl font-semibold leading-tight tracking-tight">
              {selectedItem.previewTitle}
            </h3>
            <p className="mt-3 text-sm leading-6 text-slate-600">{selectedItem.previewBody}</p>
            <div className="mt-5 divide-y divide-slate-200 border-y border-slate-200">
              {selectedItem.previewSteps.map(step => (
                <div
                  key={step}
                  className="flex gap-3 py-3 text-sm font-medium leading-5 text-slate-700"
                >
                  <span className="mt-2 inline-flex w-2 shrink-0 justify-center" aria-hidden="true">
                    <span className="size-1.5 rounded-full bg-blue" />
                  </span>
                  <span>{step}</span>
                </div>
              ))}
            </div>
            <PreviewActions item={selectedItem} onAction={setPreviewAction} />
            {previewAction ? (
              <p className="mt-3 rounded-2xl border border-blue/10 bg-blue/[0.035] px-3 py-2 text-xs font-medium leading-5 text-slate-600">
                {getPreviewNotice(selectedItem, previewAction)}
              </p>
            ) : null}
          </div>
        </aside>
      </div>
    </div>
  );
}
